import React, { useContext, useMemo, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import awsLogo from "@/assets/img/aws_logo.svg";
import azureLogo from "@/assets/img/azurelogo.svg";
import gcpLogo from "@/assets/img/gcp_logo.svg";
import billingStyles from "@/page-components/billing-credit/billing-credit.module.css";
import tooltipStyles from "@/page-components/idle-resource/idle.module.css";
import ScenariosErrorState from "@/components/States/scenariosErrorState";
import { RiContexts } from "@/page-components/RightRecommendation/right.context";
import BarGraphErrorStates from "@/components/States/BarGraphErrorStates";
import CurrencyFormatter from "@/lib/value-converison";
import config from "@/config";
import { useRecommStackedData } from "@/services/rightRecommendation/get-all-cloud-recomm-bar-chart.repo";
import styles from "@/page-components/RightRecommendation/right-recommendation.module.css";
import { CSVLink } from "react-csv";
import downlaodVM from "@/assets/img/downloadVM.svg";
import { OverlayTrigger, Tooltip as ReactTooltip } from "react-bootstrap";

const AllCloudRecommBarChart = ({ service }) => {
  const [hoveredStack, setHoveredStack] = useState(null);
  const csvLinkRef = useRef(null);

  const context = useContext(RiContexts);

  const query = useMemo(() => {
    const queryData = {
      service: service,
    };

    return queryData;
  }, [service]);

  const { data, isLoading, error } = useRecommStackedData(query);

  // Transform for grouped bar chart
  const transformData = (data = {}) => {
    return [
      {
        name: "Recommendations",
        awsRiRecommendation: data.aws?.find((d) => d.type === "ri")?.cost || 0,
        awsRightsizingRecommendation:
          data.aws?.find((d) => d.type === "rightsizing")?.cost || 0,
        azureRiRecommendation:
          data.azure?.find((d) => d.type === "ri")?.cost || 0,
        azureRightsizingRecommendation:
          data.azure?.find((d) => d.type === "rightsizing")?.cost || 0,
        gcpRiRecommendation: data.gcp?.find((d) => d.type === "ri")?.cost || 0,
        gcpRightsizingRecommendation:
          data.gcp?.find((d) => d.type === "rightsizing")?.cost || 0,
      },
    ];
  };

  const transformedData = transformData(data);

  // Check if data exists per cloud
  const hasAWSData =
    config.enabledClouds.has("aws") &&
    (transformedData[0].awsRiRecommendation > 0 ||
      transformedData[0].awsRightsizingRecommendation > 0);

  const hasAzureData =
    config.enabledClouds.has("azure") &&
    (transformedData[0].azureRiRecommendation > 0 ||
      transformedData[0].azureRightsizingRecommendation > 0);

  const hasGCPData =
    config.enabledClouds.has("gcp") &&
    (transformedData[0].gcpRiRecommendation > 0 ||
      transformedData[0].gcpRightsizingRecommendation > 0);

  // Bar configuration (unchanged)
  const groupedBarConfigurations = {
    ...(hasAWSData && {
      AWS: [
        { label: "RI", dataKey: "awsRiRecommendation", fill: "#FFE1B3" },
        {
          label: "Rightsizing",
          dataKey: "awsRightsizingRecommendation",
          fill: "#FF9900",
        },
      ],
    }),
    ...(hasAzureData && {
      Azure: [
        { label: "RI", dataKey: "azureRiRecommendation", fill: "#BCD7E9" },
        {
          label: "Rightsizing",
          dataKey: "azureRightsizingRecommendation",
          fill: "#1F78B4",
        },
      ],
    }),
    ...(hasGCPData && {
      GCP: [
        { label: "RI", dataKey: "gcpRiRecommendation", fill: "#C3E6D5" },
        {
          label: "Rightsizing",
          dataKey: "gcpRightsizingRecommendation",
          fill: "#0F9D58",
        },
      ],
    }),
  };

  const CustomTooltip = ({ active, payload, label, stackId }) => {
    if (active && payload && payload.length) {
      const cloudData = transformedData.find((d) => d.name === label) || {};

      const cloudInfo = {
        AWS: {
          riRecommendation: cloudData.awsRiRecommendation,
          rightsizingRecommendation: cloudData.awsRightsizingRecommendation,
          logo: awsLogo,
        },
        Azure: {
          riRecommendation: cloudData.azureRiRecommendation,
          rightsizingRecommendation: cloudData.azureRightsizingRecommendation,
          logo: azureLogo,
        },
        GCP: {
          riRecommendation: cloudData.gcpRiRecommendation,
          rightsizingRecommendation: cloudData.gcpRightsizingRecommendation,
          logo: gcpLogo,
        },
      };

      if (!stackId || !config.enabledClouds.has(stackId.toLowerCase()))
        return null;
      const { riRecommendation, rightsizingRecommendation, logo } =
        cloudInfo[stackId] || {};

      return (
        <div
          className={tooltipStyles.custom_tooltip}
          style={{ width: "275px" }}
        >
          <div
            className={tooltipStyles.tooltip_date}
            // style={{ paddingLeft: "8px" }}
          >
            <div className={tooltipStyles.tooltip_date_css}>{label}</div>
          </div>

          <table style={{ width: "92%", margin: "0px 12px 4px 12px" }}>
            <tbody>
              <tr className={billingStyles.tooltip_row}>
                <td style={{ paddingLeft: "0px" }}>
                  <img src={logo} alt="" />
                  <span className={billingStyles.tooltip_tr_name}>
                    {stackId}
                  </span>
                </td>
              </tr>
              <tr className={billingStyles.tooltip_row}>
                <div className={billingStyles.tooltip_table_flex}>
                  <span className={billingStyles.tooltip_cloud_name}>RI</span>
                  <span className={billingStyles.tooltip_tr_value}>
                    <CurrencyFormatter value={riRecommendation} />
                  </span>
                </div>
              </tr>
              <tr className={billingStyles.tooltip_row}>
                <div className={billingStyles.tooltip_table_flex}>
                  <span className={billingStyles.tooltip_cloud_name}>
                    Rightsizing
                  </span>
                  <span className={billingStyles.tooltip_tr_value}>
                    <CurrencyFormatter value={rightsizingRecommendation} />
                  </span>
                </div>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ groupedBars }) => {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", margin: "10px 0" }}
      >
        {Object.keys(groupedBars).map((provider, index) => (
          <div
            key={index}
            style={{ display: "flex", alignItems: "center", marginRight: 40 }}
          >
            <span
              style={{
                fontWeight: 500,
                fontSize: 12,
                fontFamily: "Inter",
                marginRight: 10,
                color: "#000000",
              }}
            >
              {provider}:
            </span>
            {groupedBars[provider].map((bar, barIndex) => (
              <div
                key={barIndex}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: 15,
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: bar.fill,
                    marginRight: 5,
                    borderRadius: "2px",
                  }}
                ></div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    fontFamily: "Inter",
                    color: "#000000",
                  }}
                >
                  {bar.label}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const flattenData = () => {
    return Object.keys(data || {}).flatMap((provider) =>
      data[provider].map((entry) => ({
        cloud: provider,
        month: entry.month,
        ri: entry.ri,
        rightsizing: entry.rightsizing,
      }))
    );
  };
  const headers = [
    { label: "Cloud", key: "cloud" },
    { label: "Month", key: "month" },
    { label: "RI", key: "ri" },
    { label: "Rightsizing", key: "rightsizing" },
  ];

  const csvData = flattenData();

  const handleDownloadCsv = () => {
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  };

  return (
    <div className={billingStyles.graph_main} style={{ padding: "24px 40px" }}>
      <div className={styles.download}>
        {((!isLoading && data) || error) && (
          <div className={billingStyles.date_total}>
            <span className={billingStyles.billing_details}>
              All Cloud Recommendations
            </span>
            {/* <span className={billingStyles.total_month}>Last 6 months</span> */}
          </div>
        )}
        {!isLoading && !error && data && (
          <div className={styles.menu_icon_css1}>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <ReactTooltip id="tooltip">Download as CSV</ReactTooltip>
              }
            >
              <span
                className={styles.downloadButton}
                onClick={handleDownloadCsv}
                data-testid="download-button"
              >
                <img src={downlaodVM} alt="" />
              </span>
            </OverlayTrigger>
            <CSVLink
              data={transformedData}
              filename={`allCloud-recomendation-data.csv`}
              target="_blank"
              ref={csvLinkRef}
              style={{ display: "none" }}
            >
              Download
            </CSVLink>
          </div>
        )}
      </div>

      {isLoading && <BarGraphErrorStates />}
      {error && (
        <ScenariosErrorState error={error.message} messageConfig={{}} />
      )}
      {data &&
        (data?.aws?.length > 0 ||
          data?.azure?.length > 0 ||
          data?.gcp?.length > 0) && (
          <div style={{ width: "100%" }}>
            <ResponsiveContainer height={300} width="100%">
              <BarChart
                width={500}
                height={300}
                data={transformedData}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0,
                }}
              >
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  fontSize={10}
                  fontFamily="Inter"
                  fontWeight={400}
                  stroke="#A3A3A3"
                />
                <YAxis
                  label={{ fill: "#A3A3A3" }}
                  tickLine={false}
                  axisLine={false}
                  fontSize={10}
                  fontFamily="Inter"
                  fontWeight={400}
                  stroke="#A3A3A3"
                />
                <CartesianGrid strokeDasharray="0" stroke="#F0F0F0" />
                <Tooltip
                  content={<CustomTooltip stackId={hoveredStack} />}
                  cursor={{ fill: "transparent" }}
                />
                {Object.keys(groupedBarConfigurations).map((provider) =>
                  groupedBarConfigurations[provider].map((bar, index) => (
                    <Bar
                      key={`${provider}-${index}`}
                      // barSize={100}
                      dataKey={bar.dataKey}
                      fill={bar.fill}
                      stackId={provider}
                      name={bar.label}
                      onMouseEnter={() => setHoveredStack(provider)}
                      barSize={300}
                    />
                  ))
                )}
              </BarChart>
              {Object.keys(groupedBarConfigurations).length > 0 && (
                <CustomLegend groupedBars={groupedBarConfigurations} />
              )}
            </ResponsiveContainer>
          </div>
        )}
    </div>
  );
};

export default AllCloudRecommBarChart;
